import React, { useState, useRef, useEffect } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { TimelineItemType } from 'types/TimelineTypes';
import { UserType } from '../../views/users/UserTypes';
import { Task, TaskType } from '../../views/tasks/TaskTypes';
import axios from 'axios';
import { saveData } from 'services/api/saveData';
import { formatTimelineDate, determineTaskDateClass } from 'services/utils/dateTimeUtils';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTrash, faPen, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { iconMapping } from 'services/utils/iconMapping';
import TaskForm from '../../views/tasks/TaskForm';
import TaskCheckmark from 'assets/TaskCheckmark.svg';
import { useTranslation } from 'react-i18next';
import '../../style/scss/check-button.scss';
import '../../style/scss/tooltip.scss';

interface TimelineTaskProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
    task: Task;
    timeline_page: string;
    timeline_page_id: number;
    assignee?: UserType | null;
    tasktype?: TaskType | null;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const TimelineTask: React.FC<TimelineTaskProps> = ({ 
    placement, item, task, timeline_page_id, timeline_page, assignee, tasktype, onSubmitSuccess
}) => {
    const { t } = useTranslation();
    const { userHash } = useAuthContext();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const { initializeModal } = useModal();
    const [isEditing, setIsEditing] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isExpandable, setIsExpandable] = useState(false);
    const textRef = useRef<HTMLParagraphElement>(null);
    const helperTextRef = useRef<HTMLDivElement>(null);
    
    // Format the date and determine the overdue/today date class
    const taskIsWholeDay = task.date !== null && task.start_datetime === null;
    const taskDate = formatTimelineDate(item.ordering_date, taskIsWholeDay);
    const dateClass = determineTaskDateClass(item.ordering_date, taskIsWholeDay, isChecked);

    // Expand the note of the task
    const toggleIsExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Shows the text expand icon if the helpertext element is higher than the visible text element
    useEffect(() => {
        const timer = setTimeout(() => {
            if (textRef.current && helperTextRef.current) {
                const visibleTextHeight = textRef.current.offsetHeight;
                const helperTextHeight = helperTextRef.current.offsetHeight;
                setIsExpandable(helperTextHeight > visibleTextHeight);
            }
        }, 100);
    
        return () => clearTimeout(timer);
    }, [task.note]);

    // Determine the task icon
    const taskIcon = tasktype?.icon ? iconMapping(tasktype.icon) : null;

    // Set the check status on initialisation
    useEffect(() => {
        setIsChecked(task.checked);
    }, [task]);

    // Update the check state in the database
    const handleCheck = async () => {

        // Convert the current date time into utc based on the timezone of the user
        const currentDateTime = new Date();
        const utcDateTime = convertLocalDateTimeToUTC(currentDateTime, userLocale, userTimezone);

        // Configure the submit data
        const submitData = {
            checked: !isChecked,
            checked_at: !isChecked ? utcDateTime : null,
            checked_by_user_hash: !isChecked ? userHash : null,
            ...(timeline_page ? { [timeline_page as string]: timeline_page_id } : {})
        }

        try {
            // Call the backend to update the task
            const response = await saveData({ 
                apiUrl: `patch_task/${task.id}`, 
                method: 'patch',
                data: submitData
            })

            // Handle successful response
            if (response?.status === 200) {

                // Determine the right alert message
                const alertMessage = !isChecked ? 'task.general.task_checked_alert' : 'task.general.task_unchecked_alert';

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'task'
                });

                // Show success alert
                setFloatingAlert({ type: 'success', message: alertMessage });

                // Update checked state
                setIsChecked(prevIsChecked => !prevIsChecked);
            }
        } catch (error) {
            console.error('Failed to update check state', error)
        }
    };

    // Handle editing of a task
    const handleEditing = () => {
        setIsEditing(true);

        if (placement === 'summary') {
            initializeModal(
                <TaskForm
                    placement='modal'
                    timeline_page={timeline_page}
                    timeline_page_id={timeline_page_id}
                    taskId={item.timeline_object_id}
                    onSubmitSuccess={onSubmitSuccess}
                />, 
                { 
                    modalSize: 'small-medium',
                    title: t('button.edit_object_label', { object_name: t('task.general.object_name.singular') }),
                    onClose: () => setIsEditing(false)
                }
            );
        }
    };

    // Delete the task in the database
    const deleteTask = async () => {
        try {
            // Call the delete task function on the backend
            const response = await saveData({ 
                apiUrl: `delete_task/${task.id}`,
                method: 'patch',
                data: {},
                source: axios.CancelToken.source()
            })

            // Handle successful response
            if (response && response.status === 200) {
                // Show success alert
                setFloatingAlert({ 'type': 'success' });

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'task'
                });
            }
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'timeline.task.alert.delete_task_failed_message' })
        }
    };

    return (
        <div className='timeline-item task'>
            {placement === 'page' && (
                <div className='marker-container'>
                    <div className="big-marker">
                        <FontAwesomeIcon icon={taskIcon || faCheck} className='note-icon fa-fw' />
                    </div>
                </div>
            )}
            <div className={`task-content ${isEditing ? 'editing' : ''}`}>
                {isEditing && placement === 'page' ? (
                    <TaskForm
                        placement='inline'
                        timeline_page={timeline_page}
                        timeline_page_id={item.timeline_page_id}
                        taskId={item.timeline_object_id}
                        closeForm={() => setIsEditing(false)}
                        onSubmitSuccess={onSubmitSuccess}
                    />
                ) : (
                    <>
                        <div className='task-header'>
                            <div className='task-title-wrapper'>
                                <div className='check-button-wrapper'>
                                    <button className={`check-button ${isChecked ? 'checked' : ''} tooltip-icon`}
                                            onClick={() => {handleCheck()}}>
                                        {isChecked ? <img src={TaskCheckmark} alt="Checked" /> : null}
                                        <span className="tooltip">{!isChecked ? t('timeline.task.general.mark_done_label') : t('timeline.task.general.mark_to_do_label')}</span>
                                    </button>
                                </div>
                                <div className='task-title'
                                    onClick={handleEditing}>
                                    {task.subject}
                                </div>
                            </div>
                            <div className='task-info'>
                                <div className='left-items'>
                                    <span className={`${dateClass}`}>
                                        {taskDate}
                                    </span>
                                    {assignee && assignee.full_name && (
                                        <>
                                            <span style={{ margin: '0 5px' }}>•</span>
                                            <span>{assignee.full_name}</span>
                                        </>
                                    )}
                                </div>
                                {placement === 'page' && (
                                    <div className='right-items'>
                                        <div className='tooltip-icon edit-icon'
                                            onClick={handleEditing}>
                                                <FontAwesomeIcon icon={faPen} />
                                                <span className="tooltip">{t('general.edit_label')}</span>
                                        </div>
                                        <div className='tooltip-icon delete-icon'
                                            onClick={deleteTask}>
                                                <FontAwesomeIcon icon={faTrash} />
                                                <span className="tooltip">{t('general.delete')}</span>
                                        </div>  
                                    </div>
                                )}
                            </div>
                        </div>
                        {task.note && (
                            <div className='task-note'>
                                <div className='left-items' style={{ position: 'relative' }}>
                                    <p className='note-text'
                                       ref={textRef} 
                                       dangerouslySetInnerHTML={{ __html: task.note }}
                                       style={!isExpanded ? { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: '1', WebkitBoxOrient: 'vertical' } : {}}>
                                    </p>
                                    {/* Help element to determine if the text expand icon needs to show */}
                                    <p className='note-text' 
                                       ref={helperTextRef} 
                                       style={{ position: 'absolute', visibility: 'hidden', height: 'auto', width: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                       dangerouslySetInnerHTML={{ __html: task.note }}>
                                    </p>
                                </div>
                                <div className='right-items'>
                                    {isExpandable && !isEditing &&
                                        <div className='tooltip-icon expand-icon' onClick={toggleIsExpanded}>
                                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                            <span className="tooltip">{isExpanded ? t('general.shrink_label') : t('general.expand_label')}</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TimelineTask;